export const REMINDERS_CHANGED_EVENT =
  'wolf-forge-reminders-changed'

export type ReminderType =
  | 'follow-up'
  | 'call'
  | 'message'
  | 'email'
  | 'other'

export type ReminderStatus =
  | 'scheduled'
  | 'done'
  | 'cancelled'

export type CrmReminder = {
  id: string
  leadId: string
  company: string
  reminderType: ReminderType
  note: string
  scheduledFor: string
  timezone: string
  notify: boolean
  status: ReminderStatus
  notifiedAt: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export type CreateReminderInput = {
  leadId: string
  company: string
  reminderType: ReminderType
  note: string
  scheduledFor: string
  timezone: string
  notify: boolean
}

export type UpdateReminderInput = {
  id: string
  leadId?: string
  company?: string
  reminderType?: ReminderType
  note?: string
  scheduledFor?: string
  timezone?: string
  notify?: boolean
  status?: ReminderStatus
}

type ReminderPayload = {
  reminder?: CrmReminder
  message?: string
}

type ReminderListPayload = {
  reminders?: CrmReminder[]
  message?: string
}

function announceReminderChange() {
  window.dispatchEvent(
    new CustomEvent(
      REMINDERS_CHANGED_EVENT,
    ),
  )
}

async function readJson<T>(
  response: Response,
): Promise<T> {
  try {
    return await response.json() as T
  } catch {
    return {} as T
  }
}

export async function listReminders():
Promise<CrmReminder[]> {
  const response = await fetch(
    '/api/reminders',
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    },
  )

  const payload =
    await readJson<ReminderListPayload>(
      response,
    )

  if (!response.ok) {
    throw new Error(
      payload.message ??
        'Reminders could not be loaded.',
    )
  }

  return Array.isArray(
    payload.reminders,
  )
    ? payload.reminders
    : []
}

export async function createReminder(
  input: CreateReminderInput,
): Promise<CrmReminder> {
  const response = await fetch(
    '/api/reminders',
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type':
          'application/json',
      },
      body: JSON.stringify(input),
    },
  )

  const payload =
    await readJson<ReminderPayload>(
      response,
    )

  if (
    !response.ok ||
    !payload.reminder
  ) {
    throw new Error(
      payload.message ??
        'The reminder could not be saved.',
    )
  }

  announceReminderChange()

  return payload.reminder
}

export async function updateReminder(
  input: UpdateReminderInput,
): Promise<CrmReminder> {
  const response = await fetch(
    '/api/reminders',
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type':
          'application/json',
      },
      body: JSON.stringify(input),
    },
  )

  const payload =
    await readJson<ReminderPayload>(
      response,
    )

  if (
    !response.ok ||
    !payload.reminder
  ) {
    throw new Error(
      payload.message ??
        'The reminder could not be updated.',
    )
  }

  announceReminderChange()

  return payload.reminder
}

export async function deleteReminder(
  id: string,
): Promise<CrmReminder> {
  const response = await fetch(
    '/api/reminders',
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        accept: 'application/json',
        'content-type':
          'application/json',
      },
      body: JSON.stringify({ id }),
    },
  )

  const payload =
    await readJson<ReminderPayload>(
      response,
    )

  if (
    !response.ok ||
    !payload.reminder
  ) {
    throw new Error(
      payload.message ??
        'The reminder could not be deleted.',
    )
  }

  announceReminderChange()

  return payload.reminder
}